import {
  useRemovePaymentMethodV2Mutation,
  useUpdateServiceActivationsV2Mutation,
} from '../../../../../codegen/vehicles';
import { useCustomIntl } from '../../../../../i18n/i18n';
import Dialog from '@rio-cloud/rio-uikit/lib/es/Dialog';
import { useAppDispatch, useAppSelector } from '../../../../../state/hooks';
import { useServiceConfigurationFormContext } from '../serviceConfigurationDialog/ServiceConfigurationForm';
import {
  closeRemoveConfirmationDialog,
  getRemoveConfirmationDialog,
  getSelectedVehicleId,
} from '../../../state/onboardingUiSlice';
import { RemoveConfirmationDialogBody } from './RemoveConfirmationDialogBody';
import { RemoveConfirmationDialogFooter } from './RemoveConfirmationDialogFooter';
import { useLoadedVehicles } from '../../../../../state/vehicles/useVehicles';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import mapValues from 'lodash/mapValues';

export const RemoveConfirmationDialog = () => {
  const dispatch = useAppDispatch();
  const intl = useCustomIntl();

  const selectedVehicleId = useAppSelector(getSelectedVehicleId);
  const {
    serviceActivationsWithPaymentMethod,
    paymentMethodId,
    open: showDialog,
  } = useAppSelector(getRemoveConfirmationDialog);

  const { removePaymentMethod } = useLoadedVehicles();

  const { getServiceActivations, removePaymentMethodFromServices } =
    useServiceConfigurationFormContext();

  const [
    callUpdateServiceActivationsMutation,
    { isLoading: isUpdateServiceActivationLoading },
  ] = useUpdateServiceActivationsV2Mutation();

  const [
    callRemovePaymentMethodMutation,
    { isLoading: isRemovePaymentMethodLoading },
  ] = useRemovePaymentMethodV2Mutation();

  const closeDialog = () => {
    dispatch(closeRemoveConfirmationDialog());
  };

  const handleSubmit = async () => {
    if (!isEmpty(serviceActivationsWithPaymentMethod)) {
      const servicesToDeactivate = map(
        serviceActivationsWithPaymentMethod,
        ({ serviceType }) => serviceType.toString(),
      );
      const serviceActivationUpdate = mapValues(
        getServiceActivations(),
        (serviceConfiguration, serviceType) =>
          servicesToDeactivate.includes(serviceType)
            ? {
                ...serviceConfiguration,
                enabled: false,
                paymentMethodAssignments: [],
              }
            : serviceConfiguration,
      );
      const serviceActivationResult =
        await callUpdateServiceActivationsMutation({
          vehicleId: selectedVehicleId!,
          updateServiceActivationsRequest: {
            serviceActivations: Object.values(serviceActivationUpdate),
          },
        });

      if (!('data' in serviceActivationResult)) {
        return;
      }
    }

    const removePaymentMethodResult = await callRemovePaymentMethodMutation({
      vehicleId: selectedVehicleId!,
      paymentMethodId,
    });

    if (!('data' in removePaymentMethodResult)) {
      return;
    }

    removePaymentMethod(selectedVehicleId!, paymentMethodId);
    updateFormContext();
    closeDialog();
  };

  const updateFormContext = () => {
    const serviceTypes = serviceActivationsWithPaymentMethod.map(
      (s) => s.serviceType,
    );
    removePaymentMethodFromServices(serviceTypes, paymentMethodId);
  };

  if (!showDialog || !paymentMethodId) {
    return <></>;
  }

  return (
    <Dialog
      data-testid={'delete-payment-method-warning-dialog'}
      useOverflow
      bsSize={Dialog.SIZE_SM}
      show
      onClose={closeDialog}
      title={intl.formatMessage({
        id: 'onboarding.dialog.removeCardConfirmation.title',
      })}
      body={
        <RemoveConfirmationDialogBody
          serviceActivations={serviceActivationsWithPaymentMethod}
        />
      }
      footer={
        <RemoveConfirmationDialogFooter
          isLoading={
            isUpdateServiceActivationLoading || isRemovePaymentMethodLoading
          }
          onClose={closeDialog}
          onSubmit={handleSubmit}
        />
      }
    />
  );
};
